/**
 * The rights vocabulary, shared by the Designer role editor and the App role
 * card so both describe a role with the same words.
 *
 * Two inputs converge on one description: the Designer edits a role TREE NODE
 * (`roleBaseLevel` + per-object `rolePermissions`), the App reads the
 * MATERIALIZED `rights` JSON that `core/rbac/role_translate.py` produced from
 * that node. Pure — no React, no network.
 */

export type BaseLevel = 'none' | 'read_all' | 'full_access'

export type RightsOp = 'read' | 'create' | 'update' | 'delete' | 'post'

export const RIGHTS_OPS: readonly RightsOp[] = ['read', 'create', 'update', 'delete', 'post']

export const OP_LABELS: Record<RightsOp, string> = {
  read: 'Read',
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  post: 'Post',
}

export interface RightsException {
  objectName: string
  ops: RightsOp[]
}

export interface RightsDescription {
  base: BaseLevel
  /** Per-object grants on top of the base level, sorted by object name. */
  exceptions: RightsException[]
  designerAccess: boolean
  userAdmin: boolean
  /** True when the materialized rights could not be parsed (App side only). */
  malformed: boolean
}

const OP_ORDER: Record<string, number> = RIGHTS_OPS.reduce(
  (acc, op, i) => ({ ...acc, [op]: i }),
  {} as Record<string, number>,
)

function isOp(value: unknown): value is RightsOp {
  return typeof value === 'string' && (RIGHTS_OPS as readonly string[]).includes(value)
}

function sortOps(ops: RightsOp[]): RightsOp[] {
  return [...ops].sort((a, b) => OP_ORDER[a] - OP_ORDER[b])
}

function toExceptions(map: Record<string, RightsOp[]>): RightsException[] {
  return Object.keys(map)
    .sort()
    .map((objectName) => ({ objectName, ops: sortOps(map[objectName]) }))
}

/** Designer side: the role tree node. */
export function describeRoleNode(node: {
  roleBaseLevel?: BaseLevel
  rolePermissions?: Array<{ object_name: string; permission: string }>
  roleDesignerAccess?: boolean
  roleUserAdmin?: boolean
}): RightsDescription {
  const map: Record<string, RightsOp[]> = {}
  for (const grant of node.rolePermissions ?? []) {
    // A wildcard entry is not authorable any more (the base level replaced it)
    // and the backend translator ignores it too — skip rather than invent a
    // meaning for it.
    if (!grant?.object_name || grant.object_name === '*') continue
    if (!isOp(grant.permission)) continue
    const bucket = map[grant.object_name] ?? (map[grant.object_name] = [])
    if (!bucket.includes(grant.permission)) bucket.push(grant.permission)
  }
  return {
    base: node.roleBaseLevel ?? 'none',
    exceptions: toExceptions(map),
    designerAccess: Boolean(node.roleDesignerAccess),
    userAdmin: Boolean(node.roleUserAdmin),
    malformed: false,
  }
}

const EMPTY: RightsDescription = {
  base: 'none',
  exceptions: [],
  designerAccess: false,
  userAdmin: false,
  malformed: true,
}

/** App side: the materialized `rights` JSON string (or an already-parsed object). */
export function describeRightsJson(
  rights: string | Record<string, unknown> | null | undefined,
): RightsDescription {
  let parsed: unknown = rights
  if (typeof rights === 'string') {
    try {
      parsed = JSON.parse(rights)
    } catch {
      return { ...EMPTY }
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ...EMPTY }
  }
  const obj = parsed as Record<string, unknown>
  const fullAccess = Boolean(obj.full_access)
  const map: Record<string, RightsOp[]> = {}
  const rawObjects = obj.objects
  if (rawObjects && typeof rawObjects === 'object' && !Array.isArray(rawObjects)) {
    for (const [name, ops] of Object.entries(rawObjects as Record<string, unknown>)) {
      if (!Array.isArray(ops)) continue
      map[name] = ops.filter(isOp)
    }
  }
  return {
    base: fullAccess ? 'full_access' : Boolean(obj.read_all) ? 'read_all' : 'none',
    exceptions: toExceptions(map),
    designerAccess: Boolean(obj.designer_access),
    userAdmin: Boolean(obj.user_admin),
    malformed: false,
  }
}

/** One-line human summary of what a role allows. */
export function summarizeRights(d: RightsDescription): string {
  if (d.malformed) return 'Rights cannot be read'
  if (d.base === 'full_access') return 'Full access'
  const count = d.exceptions.length
  const objects = `${count} object${count === 1 ? '' : 's'}`
  if (d.base === 'read_all') {
    return count === 0 ? 'Read-only everywhere' : `Read-only everywhere, plus changes to ${objects}`
  }
  return count === 0 ? 'No access' : `Access to ${objects}`
}
